import { palette } from '../styles/branding'
import {
  MAX_STEPS_TO_SHOW,
  STEP_TYPE_CONNECTION,
  MINUTE,
  MAX_LINES_TO_SHOW,
} from '../constants'
import uniqBy from 'lodash.uniqby'
import isEmpty from 'lodash.isempty'

export const primaryTextColor = (queryParam) => {
  switch (queryParam.inverse) {
    case 'white':
      return palette.neutral1

    case 'yellow':
      return palette.neutral1

    default:
      return palette.dark
  }
}

export const changeColorMode = (color) => {
  switch (color) {
    case 'white':
      return false

    case 'yellow':
      return false

    default:
      return true
  }
}
export const getDepartureTimeColor = (queryParam, isDeleted) => {
  if (isDeleted) {
    return '#ff8080'
  }
  switch (queryParam.inverse) {
    case 'white':
      return palette.neutral1

    case 'yellow':
      return palette.neutral1

    default:
      return palette.dark
  }
}

export const secondaryTextColor = (queryParam) => {
  switch (queryParam.inverse) {
    case 'white':
      return '#cccccc'

    case 'yellow':
      return '#cccccc'

    default:
      return palette.dark
  }
}

export const reload = () => {
  window.location.reload(true)
}

export const getDepartureTimestamp = (line, serverTijd) => {
  const isRealtime = line.predictionStatussen.includes('REALTIME')
  const departureTime = Math.round(
    ((line.vertrekRealtimeTijdstip || line.vertrekTheoretischeTijdstip) -
      serverTijd) /
      MINUTE,
  )

  // to avoid "negative integers"
  return departureTime <= 0
    ? {
        departureTime: 0,
        isRealtime,
      }
    : {
        departureTime,
        isRealtime,
      }
}

export const mapInfotramRealtimePassages = (data) => {
  const {
    lijnen = [],
    serverTijd,
    halteOmleidingen = [],
    storingen = [],
  } = data || {}
  const filteredLines = lijnen
    .filter((line) => !line.predictionDeleted)
    .reduce((acc, val) => {
      return {
        ...acc,
        [val.lijnNummer]: acc[val.lijnNummer]
          ? [...acc[val.lijnNummer], getDepartureTimestamp(val, serverTijd)]
          : [getDepartureTimestamp(val, serverTijd)],
      }
    }, {})
  return { lijnen: filteredLines, halteOmleidingen, storingen }
}

export const compareInfotramPassages = (newApiData, oldApiData) => {
  if (isEmpty(oldApiData)) {
    return newApiData
  }
  const [{ lijnen: newLijnen, serverTijd, halteOmleidingen, storingen }] =
    newApiData.halteDoorkomstenLijst
  const [{ lijnen: oldLijnen }] = oldApiData.halteDoorkomstenLijst
  const comparedPassages = newLijnen.map((passage) => {
    const newPassageId = `${passage.vertrekTheoretischeTijdstip}-${passage.ritNummer}`

    const oldPassage = oldLijnen.find(
      ({ vertrekTheoretischeTijdstip, ritNummer }) =>
        `${vertrekTheoretischeTijdstip}-${ritNummer}` === newPassageId,
    )

    if (oldPassage) {
      const oldPassageArrivesIn = Math.round(
        (oldPassage.vertrekRealtimeTijdstip - serverTijd) / 60000,
      )
      const newPassageArrivesIn = Math.round(
        (passage.vertrekRealtimeTijdstip - serverTijd) / 60000,
      )
      // If the new passage arrives later than the previous passage,
      // return the previous passage to prevent backward jumps
      if (oldPassageArrivesIn < newPassageArrivesIn) {
        return oldPassage
      }
    }
    return passage
  })
  return { lijnen: comparedPassages, serverTijd, halteOmleidingen, storingen }
}

export const filterPassedPassages = (passages) => {
  return passages
    .filter((passage) => passage.departureTime !== 0)
    .map((passage) => {
      return {
        departureTime: passage.departureTime - 1,
        isRealtime: false,
      }
    })
}

export function cleanDestinationName(destination) {
  if (!destination.includes('-')) return destination
  const [, cleanDestination] = destination.split('-')
  return cleanDestination
}
export function getPreviousStopsWithTime({ ritten = [] }, haltenummer) {
  for (const rit of ritten) {
    const { doorkomsten } = rit
    const index = doorkomsten.findIndex(
      (stop) => stop.haltenummer === haltenummer,
    )
    const currentAndPreviousStops = doorkomsten.slice(0, index + 1)
    if (!currentAndPreviousStops.find((stop) => !stop.dienstregelingTijdstip)) {
      return convertLineStopsToSteps(currentAndPreviousStops)
    }
  }
  return { hasMorePreviousSteps: false, steps: [] }
}

export function getActualTheoreticTrip(
  trips,
  stopNumber,
  serverTime = Date.now(),
) {
  for (const trip of trips) {
    const { doorkomsten } = trip
    const filtredPassages = doorkomsten.filter((d) => d.dienstregelingTijdstip)
    const foundPassage = filtredPassages.find(
      (d) => d.haltenummer === stopNumber,
    )
    const dienstregelingTijdstip = foundPassage?.dienstregelingTijdstip
    if (dienstregelingTijdstip && dienstregelingTijdstip > serverTime) {
      const stopIndex = filtredPassages.findIndex(
        (stop) => stop.haltenummer === stopNumber,
      )
      const currentAndPreviousStops = filtredPassages.slice(0, stopIndex + 1)
      return convertLineStopsToSteps(currentAndPreviousStops)
    }
  }
  return { hasMorePreviousSteps: false, steps: [] }
}

export function convertLineStopsToSteps(stops) {
  const minute = 60000
  let previousStop
  let steps = stops
    .map((stop, index) => {
      if (index === 0) {
        previousStop = stop
        return stop
      }
      const differenceInMinutes =
        (stop.dienstregelingTijdstip - previousStop.dienstregelingTijdstip) /
        minute
      if (differenceInMinutes > 1) {
        previousStop = stop

        return [
          Array(differenceInMinutes - 1).fill({ type: STEP_TYPE_CONNECTION }),
          stop,
        ].flat()
      }
      previousStop = stop
      return stop
    })
    .flat()

  const maximumStepsToShow = steps.slice(-MAX_STEPS_TO_SHOW)
  return {
    hasMorePreviousSteps: steps.length > maximumStepsToShow.length,
    steps: maximumStepsToShow,
  }
}

export function mapStopsByLine(linesStops) {
  return linesStops.reduce((acc, { lijnNummer, data }) => {
    return {
      ...acc,
      [lijnNummer]: data,
    }
  }, {})
}

export function mapStopNamesWithStopNumbers(stepsByLine, stopsByLine) {
  return Object.entries(stepsByLine).reduce(
    (acc, [line, { hasMorePreviousSteps, steps }]) => {
      const stops = stopsByLine[line]
      return {
        ...acc,
        [line]: {
          hasMorePreviousSteps,
          steps: mapStopNames(steps, stops),
        },
      }
    },
    {},
  )
}

export function mapStopNames(steps, stops) {
  return steps.map((step) => {
    if (step.type === STEP_TYPE_CONNECTION) {
      return step
    }
    if (!stops) {
      return step
    }
    const matchedStop = stops.find(
      (l) => l.halteNummer === parseInt(step.haltenummer, 10),
    )
    const omschrijvingKort = matchedStop?.omschrijvingKort || ''
    return {
      haltenummer: step.haltenummer,
      halteNaam: omschrijvingKort,
    }
  })
}

export function mapTheoreticPassagesByLine(passages, serverTime) {
  return passages.reduce((acc, { tijdstip, lijn: { lijnNummer } }) => {
    const passagesByLine = acc[lijnNummer]
    const date = determineTimestampByTijdstip(tijdstip, serverTime)
    return {
      ...acc,
      [lijnNummer]: passagesByLine ? [...passagesByLine, date] : [date],
    }
  }, {})
}

export function getActualTheoreticTripByLine(
  theoreticTripsByLine,
  stopNumber,
  serverTime = Date.now(),
) {
  if (!theoreticTripsByLine) {
    return {}
  }
  return Object.entries(theoreticTripsByLine).reduce(
    (acc, [lijn, ritten]) => ({
      ...acc,
      [lijn]: getActualTheoreticTrip(ritten, stopNumber, serverTime),
    }),
    {},
  )
}

export function determineTimestampByTijdstip(
  tijdstip,
  serverTime = Date.now(),
) {
  const ONE_DAY_IN_MS = 86400000
  const [hoursString, minutesString] = tijdstip.split(':')
  const hours = parseInt(hoursString, 10)
  const minutes = parseInt(minutesString, 10)
  const date = new Date(serverTime) // @todo use serverTime
  // If data is fetched between 00:00 & 03:59, subtract 1 day for each passage
  if (date.getHours() < 4) {
    date.setTime(date.getTime() - ONE_DAY_IN_MS)
  }
  // Every passage between 00:00 & 03:59 is considered following day (+ 1 day)
  if (hours < 4) {
    date.setTime(date.getTime() + ONE_DAY_IN_MS)
  }
  const timestamp = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
  ).getTime()
  return {
    timestamp,
    tijdstip,
  }
}

export function getFirstUpcomingTheoreticPassage(passages = []) {
  const now = Date.now()
  const upcomingPassages = passages.filter(({ timestamp }) => timestamp >= now)

  if (!upcomingPassages.length) {
    return ''
  }

  return upcomingPassages.reduce((prev, curr) =>
    Math.abs(curr - now) < Math.abs(prev - now) ? curr : prev,
  )
}
export function filterLines(lijnen, lijnenQueryParam = []) {
  const isArray = Array.isArray(lijnenQueryParam)
  if (isArray && !lijnenQueryParam.length) {
    return lijnen
  }
  return lijnen.filter((lijn) => {
    const lijnNummerPubliek = lijn.lijnNummerPubliek.toLowerCase()
    return isArray
      ? lijnenQueryParam.includes(lijnNummerPubliek)
      : lijnenQueryParam === lijnNummerPubliek
  })
}

export function getLinesFromDeviation(verstoordeLijnenSet = []) {
  if (!verstoordeLijnenSet.length) {
    return ''
  }
  const linesImpacted = uniqBy(verstoordeLijnenSet, 'lijnNummerPubliek').map(
    (verstoordeLijn) => verstoordeLijn.lijnNummerPubliek,
  )
  const hasToMuchLinesImpacted = linesImpacted.length > MAX_LINES_TO_SHOW
  const linesString = linesImpacted
    .filter((line, index) => index < MAX_LINES_TO_SHOW)
    .join(', ')

  return hasToMuchLinesImpacted
    ? `${linesString} ...`
    : linesString.replace(/,([^,]*)$/, ' en$1')
  // replace last comma (',') with 'en'
  // or if to much impacted lines replace lines with ...
}

export function getDetours(detours) {
  return detours
    .map((detour) => {
      const [omleiding] = detour.omleidingen
      return {
        ...detour,
        title: `Storing lijn ${getLinesFromDeviation(
          detour.verstoordeLijnenSet,
        )}`,
        type: 'detour',
        id: `notification-detour-${omleiding.id}`,
      }
    })
    .sort((a, b) => {
      return a.omleidingen[0].id - b.omleidingen[0].id
    })
}
export function getDisruptions(disruptions = []) {
  if (!disruptions.length) {
    return null
  }
  return {
    disruptions,
    title: `Opgelet storing`,
    type: 'disruption',
    id: 'notification-disruptions',
  }
}
export function getImportantAnnouncement(isVisible) {
  if (!isVisible) {
    return null
  }
  return {
    title: `Belangrijke mededeling`,
    type: 'importantAnnouncement',
    id: 'notification-important-announcement',
  }
}

export function getAllAnnouncements(
  data,
  importantAnnouncementVisible,
  // eslint-disable-next-line no-unused-vars
  allUniqueDetoursByLines = [], // optional field (only Infotram)
) {
  const halteOmleidingen = (data && data.halteOmleidingen) || []
  const storingen = (data && data.storingen) || []
  // const allDetours = [...halteOmleidingen, ...allUniqueDetoursByLines]
  // console.log('Combined detours: realtime + byLines', allDetours)
  // @todo: fix dates/time in allUniqueDetoursByLines which currently crash DeviationDetail
  // const detours = getDetours(allDetours)
  const detours = getDetours(halteOmleidingen)
  const disruptions = getDisruptions(storingen)
  const importantAnnouncement = getImportantAnnouncement(
    importantAnnouncementVisible,
  )

  const allAnnouncements = [
    importantAnnouncement,
    ...detours,
    disruptions,
  ].filter(Boolean)
  return uniqBy(allAnnouncements, 'id')
}
export function getNotServedStopsByTown(notServedStops) {
  return notServedStops.reduce(
    (acc, { omschrijvingGemeente, omschrijvingKort }) => {
      return {
        ...acc,
        [omschrijvingGemeente]: acc[omschrijvingGemeente]
          ? [...acc[omschrijvingGemeente], omschrijvingKort]
          : [omschrijvingKort],
      }
    },
    {},
  )
}

export function getColorByLineNumber(lijnNummer, notServed) {
  const pink = 'rgba(255, 149, 206, 0.5)'

  if (notServed) {
    return {
      vignette: 'rgb(239, 57, 52, 1)',
      line: 'rgb(239, 57, 52, 0.5)',
      background: 'rgb(239, 57, 52, 0.5)',
      ovalBorder: '#FB9292',
    }
  }

  const colorMap = {
    2: {
      vignette: 'rgba(34, 153, 34, 1)',
      line: 'rgba(34, 153, 34, 0.5)',
      background: 'rgba(34, 153, 34, 0.5)',
      ovalBorder: '#11c011',
    },
    3: {
      vignette: 'rgba(255, 204, 17, 1)',
      line: 'rgba(255, 204, 17, 0.5)',
      background: 'rgba(255, 204, 17, 0.5)',
      ovalBorder: '#fff199',
    },
    5: {
      vignette: 'rgba(119, 17, 51, 1)',
      line: 'rgba(197, 71, 113, 0.5)',
      background: 'rgba(217, 28, 91, 0.5)',
      ovalBorder: '#c60042;',
    },
    6: {
      vignette: 'rgba(197, 71, 113, 1)',
      line: pink,
      background: 'rgba(221, 0, 119, 0.5)',
      ovalBorder: '#ff0a8e',
    },
    8: {
      vignette: 'rgba(255, 136, 170, 1)',
      line: pink,
      background: pink,
      ovalBorder: '#ffb2c8',
    },
    9: {
      vignette: 'rgba(238, 136, 34, 1)',
      line: 'rgba(245, 186, 127, 0.5)',
      background: 'rgba(238, 136, 34, 0.5)',
      ovalBorder: '#f5ba7f',
    },
    10: {
      vignette: 'rgba(187, 221, 0, 1)',
      line: 'rgba(187, 221, 0, 0.5)',
      background: 'rgba(187, 221, 0, 0.5)',
      ovalBorder: '#829b02',
    },
    15: {
      vignette: 'rgba(68, 68, 17, 1)',
      line: 'rgba(121, 121, 14, 0.5)',
      background: 'rgba(121, 121, 14, 0.5)',
      ovalBorder: '#79790e',
    },
    defaultValue: {
      vignette: 'rgb(51, 51, 51)',
      line: 'rgba(255, 255, 255, 0.5)',
      background: 'rgba(255, 255, 255, 0.5)',
      ovalBorder: '#acacac',
    },
  }
  return colorMap[lijnNummer] || colorMap.defaultValue
}

export function sortPassages(passages) {
  return passages.sort(
    (a, b) =>
      (a.vertrekRealtimeTijdstip || a.vertrekTheoretischeTijdstip) -
      (b.vertrekRealtimeTijdstip || b.vertrekTheoretischeTijdstip),
  )
}

export const formatDate = (string = '') => {
  if (!string) {
    return
  }
  const [date, month, year] = string.split('-').map((n) => parseInt(n))
  return `${year}-${String(month).padStart(2, '0')}-${String(date).padStart(
    2,
    '0',
  )}T00:00:00`
}
export function getIndexOfFirstUpcomingPassage(
  passages,
  serverTime = Date.now(),
) {
  return passages.findIndex(
    (passage) => passage.vertrekTheoretischeTijdstip >= serverTime,
  )
}

export const getPassagesHeight = (passages) => {
  return passages.reduce((acc, val) => acc + val.offsetHeight, 0)
}
export const getErrorContainerHeight = (
  passagesContainerHeight = 912,
  sumPassagesHeight,
) => {
  if (sumPassagesHeight > passagesContainerHeight) {
    return 0
  }
  return passagesContainerHeight - sumPassagesHeight
}

export const getTheoreticalPassagesToShow = ({
  passages,
  sortedPassages,
  startIndex,
  endIndex,
  error,
  data,
  setTheoreticalPassagesToShow,
}) => {
  if (startIndex < 0) {
    return
  }

  if (error || (!passages?.lijnen?.length && data)) {
    setTheoreticalPassagesToShow(sortedPassages.slice(startIndex, endIndex))
  }
}

export const getLastElement = (passagesDOMElements) => {
  let sumOfHeightPassages = 0

  const HEADER_HIGHT =
    document.querySelector('.irtis-header')?.clientHeight +
    document.querySelector('#passage-loading-bar')?.clientHeight
  const FOOTER_HEIGHT =
    document.querySelector('.notification__header')?.clientHeight || 112
  var clientHeight = document.body.clientHeight

  const passsagesContainerHeight = clientHeight - (FOOTER_HEIGHT + HEADER_HIGHT)

  for (const passage of passagesDOMElements) {
    sumOfHeightPassages += passage.clientHeight
    if (sumOfHeightPassages + passage.clientHeight > passsagesContainerHeight) {
      return passage
    }
  }
  return null
}

export const renderBorderOnPassages = (passagesDOMElements) => {
  passagesDOMElements
    .map((passage) => {
      passage.style.borderBottomStyle = 'solid'
      passage.style.setProperty('--height', '0')
      return passage
    })
    .forEach((passage) => {
      const lastElement = getLastElement(passagesDOMElements)
      if (lastElement?.dataset?.id === passage.dataset.id) {
        // We give a bigger 'border' and remove the border-bottom  to the last passage in view
        passage.style.borderBottomStyle = 'hidden'
        passage.style.setProperty('--height', '8px')
      }
    })
}
